import { Button } from '@hub/button'
import { LinkBox, LinkOverlay } from '@hub/link'
import LinkReel from '@scentregroup/shared/hub-components/link-reel'
import { NextRouter } from 'next/router'
import React from 'react'
import { useRouter } from '../../../../../website/lib/router'
import {
  ICategory,
  ICategoryPathElement,
  ICategoryRelatedLink,
} from '../../types/marketplace-categories'
import trackClickEvent from '@scentregroup/shared/helpers/analytics-utils/track-event'
import analyticsUtils from '@scentregroup/shared/helpers/analytics-utils'
import { getAbsolutePathFromRouter } from '@scentregroup/shared/helpers/url-helpers'

interface ICategoryLinksProps {
  categories: Pick<ICategory, 'path' | 'title' | 'content'>[] | undefined
  relatedLinks: ICategoryRelatedLink[] | undefined
  isMobile?: boolean
}

type Link = {
  path: string
  label: string
  isActive?: boolean
}

const selectLinksByDisplayMode = (
  relatedLinks: ICategoryRelatedLink[] | undefined,
  displayMode: ICategoryRelatedLink['displayMode']
): Link[] => {
  return (relatedLinks || [])
    .filter(relatedLink => relatedLink.displayMode === displayMode)
    .map(relatedLink => ({
      path: relatedLink.path,
      label: relatedLink.label,
    }))
}

const selectOverrideLinks = (
  router: NextRouter,
  categories: Pick<ICategory, 'path' | 'title' | 'content'>[] | undefined,
  relatedLinks: ICategoryRelatedLink[] | undefined
): Link[] => {
  const getPathUrl = (path: ICategoryPathElement[]): string => {
    return getAbsolutePathFromRouter(router, {
      categoryIdPath: path.map(({ code }) => code),
      page: undefined,
    })
  }
  const isActivePath = (path: ICategoryPathElement[]): boolean => {
    return (
      [router?.query?.categoryIdPath].flat().join('/') ===
      path.map(({ code }) => code).join('/')
    )
  }
  return (categories || [])
    .map(category => {
      const relatedLink = relatedLinks?.find(
        relatedLink =>
          relatedLink.path === category.path.map(({ code }) => code).join('/')
      )

      if (relatedLink?.displayMode === 'Override Button Label') {
        return {
          path: getPathUrl(category.path),
          label: relatedLink.label,
          isActive: isActivePath(category.path),
        }
      }
      return {
        path: getPathUrl(category.path),
        label: category.content?.h1Header || category.title,
        isActive: isActivePath(category.path),
      }
    })
    .filter(link => link.label !== null)
}

const CategoryLinks: React.FC<React.PropsWithChildren<ICategoryLinksProps>> = ({
  categories,
  relatedLinks,
  isMobile,
}) => {
  const router = useRouter()

  const categoryLinks = selectOverrideLinks(router, categories, relatedLinks)
  const beforeLinks = selectLinksByDisplayMode(relatedLinks, 'Before')
  const afterLinks = selectLinksByDisplayMode(relatedLinks, 'After')
  const links: Link[] = beforeLinks.concat(categoryLinks).concat(afterLinks)

  if (
    categoryLinks.length <= 1 &&
    beforeLinks.length === 0 &&
    afterLinks.length === 0
  ) {
    // do not show if current category is the _only_ category
    return null
  }

  return (
    <LinkReel gap="spacing-sm" isMobile={isMobile}>
      {links.map((link, i) => (
        <LinkBox key={i} sx={{ flexGrow: 0 }}>
          <LinkOverlay href={link.path}>
            <Button
              as="span"
              isActive={link.isActive}
              variant="toggle"
              colorScheme="licorice"
              onClick={() => {
                trackClickEvent(
                  analyticsUtils.categories.MARKETPLACE,
                  analyticsUtils.actions.CLICK,
                  `Contextual Category Link ${link.label}`
                )
              }}
            >
              {link.label}
            </Button>
          </LinkOverlay>
        </LinkBox>
      ))}
    </LinkReel>
  )
}

export default CategoryLinks
